import { codeExample, defineDemoElement, demoLayoutStyles, stripIndent } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import { query, state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';
import type { WebmoduleRequestCloseEvent } from '../../events/webmodule-request-close.js';
import type WebmoduleButton from '../../components/button/button.js';
import type WebmoduleDrawer from '../../components/drawer/drawer.js';
import type WebmoduleModal from '../../components/modal/modal.js';

const FILLER_BODY =
  'This body is placeholder content - in a real screen it would hold a form, a summary or any other markup. ' +
  'The overlay sizes itself to its content, and the body scrolls when the content does not fit.';

interface DemoModalOptions {
  size?: 'small' | 'medium' | 'large';
  noHeader?: boolean;
  headerActions?: boolean;
  preventClose?: boolean;
  tallContent?: boolean;
}

/** One reusable modal, reconfigured per scenario button. */
class DemoModal extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }
    `
  ];

  @query('webmodule-modal') private modal: WebmoduleModal;
  @state() private size: 'small' | 'medium' | 'large' = 'medium';
  @state() private noHeader = false;
  @state() private headerActions = false;
  @state() private preventClose = false;
  @state() private tallContent = false;

  render() {
    return html`
      <h4>Size</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open({ size: 'small' })}>Small</webmodule-button>
        <webmodule-button @click=${() => this.open({ size: 'medium' })}>Medium</webmodule-button>
        <webmodule-button @click=${() => this.open({ size: 'large' })}>Large</webmodule-button>
      </div>

      <h4>Header</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open({ noHeader: true })}>No header</webmodule-button>
        <webmodule-button @click=${() => this.open({ headerActions: true })}>Header actions</webmodule-button>
      </div>

      <h4>Behavior</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open({ preventClose: true })}>
          Prevent close (button only)
        </webmodule-button>
        <webmodule-button @click=${() => this.open({ tallContent: true })}>Scrolling body</webmodule-button>
      </div>

      <webmodule-modal
        label="Modal example"
        size=${this.size}
        ?no-header=${this.noHeader}
        @webmodule-request-close=${(e: WebmoduleRequestCloseEvent) => {
          if (this.preventClose && e.detail.source !== 'close-button') {
            e.preventDefault();
          }
        }}
      >
        ${this.headerActions
          ? html`<webmodule-icon-button slot="header-actions" name="eye" label="Preview"></webmodule-icon-button>`
          : ''}
        <div style=${this.tallContent ? 'height: 150vh;' : ''}>
          ${FILLER_BODY}
          ${this.preventClose
            ? html`<p><strong>Overlay and Escape are vetoed - only the close button dismisses this modal.</strong></p>`
            : ''}
          ${this.noHeader
            ? html`<webmodule-button style="margin-top: 1rem;" @click=${() => this.modal.hide()}
                >Close</webmodule-button
              >`
            : ''}
        </div>
        <webmodule-button slot="footer" variant="primary" @click=${() => this.modal.hide()}>Close</webmodule-button>
      </webmodule-modal>
    `;
  }

  private open(options: DemoModalOptions) {
    this.size = options.size ?? 'medium';
    this.noHeader = options.noHeader ?? false;
    this.headerActions = options.headerActions ?? false;
    this.preventClose = options.preventClose ?? false;
    this.tallContent = options.tallContent ?? false;

    void this.updateComplete.then(() => this.modal.show());
  }
}

/** One reusable drawer with placement/containment scenarios. */
class DemoDrawer extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }

      .stage {
        position: relative;
        height: 260px;
        border: dashed 2px var(--webmodule-color-neutral-300);
        border-radius: var(--webmodule-border-radius-medium);
        padding: var(--webmodule-spacing-medium);
        margin-bottom: var(--webmodule-spacing-large);
        color: var(--webmodule-color-neutral-500);
        overflow: hidden;
      }
    `
  ];

  @query('webmodule-drawer') private drawer: WebmoduleDrawer;
  @state() private placement: 'top' | 'end' | 'bottom' | 'start' = 'end';
  @state() private contained = false;

  render() {
    return html`
      <div class="stage">
        A position: relative container - the contained drawer renders inside it.
        <webmodule-drawer label="Drawer example" placement=${this.placement} ?contained=${this.contained}>
          ${FILLER_BODY}
          <webmodule-button slot="footer" variant="primary" @click=${() => this.drawer.hide()}>Close</webmodule-button>
        </webmodule-drawer>
      </div>

      <h4>Containment</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open('end', false)}>Viewport</webmodule-button>
        <webmodule-button @click=${() => this.open('end', true)}>Contained</webmodule-button>
      </div>

      <h4>Placement</h4>
      <div class="demo-row" @click=${(e: PointerEvent) => this.openFromValue(e)}>
        <webmodule-button value="top">Top</webmodule-button>
        <webmodule-button value="end">End</webmodule-button>
        <webmodule-button value="bottom">Bottom</webmodule-button>
        <webmodule-button value="start">Start</webmodule-button>
      </div>
    `;
  }

  private openFromValue(e: PointerEvent) {
    const value = (e.target as WebmoduleButton).value as 'top' | 'end' | 'bottom' | 'start' | undefined;

    if (value) {
      this.open(value, this.contained);
    }
  }

  private open(placement: 'top' | 'end' | 'bottom' | 'start', contained: boolean) {
    this.placement = placement;
    this.contained = contained;

    void this.updateComplete.then(() => this.drawer.show());
  }
}

/** Interactive playground for the low-level popup positioning primitive. */
class DemoPopup extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }

      .stage {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        border: dashed 2px var(--webmodule-color-neutral-300);
        border-radius: var(--webmodule-border-radius-medium);
        margin-bottom: var(--webmodule-spacing-large);
      }

      .anchor {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 9rem;
        height: 3rem;
        border-radius: var(--webmodule-border-radius-medium);
        background: var(--webmodule-color-neutral-100);
        border: solid 1px var(--webmodule-color-neutral-300);
      }

      .bubble {
        padding: var(--webmodule-spacing-small) var(--webmodule-spacing-medium);
        background: var(--webmodule-color-primary-600);
        color: var(--webmodule-color-neutral-0);
        border-radius: var(--webmodule-border-radius-medium);
        font-size: var(--webmodule-font-size-small);
      }
    `
  ];

  @state() private active = true;
  @state() private placement = 'top';
  @state() private arrow = true;
  @state() private distance = 8;

  render() {
    return html`
      <div class="stage">
        <webmodule-popup
          placement=${this.placement}
          ?active=${this.active}
          ?arrow=${this.arrow}
          distance=${this.distance}
        >
          <span slot="anchor" class="anchor">Anchor</span>
          <div class="bubble">Positioned content</div>
        </webmodule-popup>
      </div>

      <div class="demo-row">
        <webmodule-toggle
          ?checked=${this.active}
          @webmodule-change=${(e: Event) =>
            (this.active = (e.target as HTMLInputElement & { checked: boolean }).checked)}
        >
          Active
        </webmodule-toggle>
        <webmodule-toggle
          ?checked=${this.arrow}
          @webmodule-change=${(e: Event) =>
            (this.arrow = (e.target as HTMLInputElement & { checked: boolean }).checked)}
        >
          Arrow
        </webmodule-toggle>
        <webmodule-select
          label="Placement"
          class="webmodule-control-left-label"
          size="small"
          style="min-width: 240px;"
          .values=${['top', 'top-start', 'top-end', 'right', 'bottom', 'bottom-start', 'bottom-end', 'left'].map(p => ({
            value: p,
            text: p
          }))}
          .value=${this.placement}
          @webmodule-change=${(e: Event) => (this.placement = (e.target as HTMLInputElement).value)}
        ></webmodule-select>
        <webmodule-input
          label="Distance"
          class="webmodule-control-left-label"
          size="small"
          type="number"
          style="width: 160px;"
          .value=${String(this.distance)}
          @webmodule-input=${(e: Event) => (this.distance = Number((e.target as HTMLInputElement).value) || 0)}
        ></webmodule-input>
      </div>
    `;
  }
}

export function registerOverlayDemos() {
  defineDemoElement('demo-modal', DemoModal);
  defineDemoElement('demo-drawer', DemoDrawer);
  defineDemoElement('demo-popup', DemoPopup);
}

export const modalSection: DemoSection = {
  id: 'modal',
  title: 'Modal',
  tags: ['webmodule-modal'],
  summary:
    'A dialog rendered above a page overlay. Closing is negotiable: listen for webmodule-request-close and call ' +
    'preventDefault() to veto specific dismissal sources.',
  render: () => html`
    <demo-example
      label="Scenarios"
      .code=${stripIndent(`
        <webmodule-modal label="Modal example" size="medium">
          Body content...
          <webmodule-icon-button slot="header-actions" name="eye" label="Preview"></webmodule-icon-button>
          <webmodule-button slot="footer" variant="primary">Close</webmodule-button>
        </webmodule-modal>

        // Veto overlay/escape dismissal:
        modal.addEventListener('webmodule-request-close', e => {
          if (e.detail.source !== 'close-button') e.preventDefault();
        });`)}
    >
      <demo-modal></demo-modal>
    </demo-example>
  `
};

export const drawerSection: DemoSection = {
  id: 'drawer',
  title: 'Drawer',
  tags: ['webmodule-drawer'],
  summary:
    'A panel that slides in from an edge of the viewport - or of its own parent when contained is set and the ' +
    'parent has position: relative.',
  render: () => html`
    <demo-example
      label="Scenarios"
      .code=${stripIndent(`
        <webmodule-drawer label="Drawer example" placement="end" contained>
          Body content...
          <webmodule-button slot="footer" variant="primary">Close</webmodule-button>
        </webmodule-drawer>`)}
    >
      <demo-drawer></demo-drawer>
    </demo-example>
  `
};

export const popupSection: DemoSection = {
  id: 'popup',
  title: 'Popup',
  tags: ['webmodule-popup'],
  summary:
    'The low-level positioning primitive behind dropdown, tooltip, select and combobox. Use it directly when ' +
    'building custom floating UI; it only positions - showing, hiding and dismissal are yours to implement.',
  render: () => html`
    <demo-example
      label="Playground"
      .code=${stripIndent(`
        <webmodule-popup placement="top" active arrow distance="8">
          <span slot="anchor">Anchor</span>
          <div>Positioned content</div>
        </webmodule-popup>`)}
    >
      <demo-popup></demo-popup>
    </demo-example>
    ${codeExample({
      label: 'Notes',
      description: html`Popup is intentionally minimal: combine <code>flip</code>, <code>shift</code> and
        <code>auto-size</code> to keep content in the viewport, and <code>hoist</code>-style fixed positioning via
        <code>strategy="fixed"</code> when clipped by overflow containers.`,
      code: `
        <!-- set active to position the content -->
        <webmodule-popup placement="bottom-start" flip shift strategy="fixed" distance="4">
          <span slot="anchor">...</span>
          <div>...</div>
        </webmodule-popup>`
    })}
  `
};

export const previewSection: DemoSection = {
  id: 'preview',
  title: 'Preview',
  tags: ['webmodule-preview'],
  summary:
    'A hover-triggered preview popup with keyboard accessibility. Shows a panel with title and scrollable content ' +
    'when the user hovers or focuses the trigger element. Dismissible via Escape key or blur.',
  render: () => html`
    ${codeExample({
      label: 'Basic usage',
      description: 'Hover or focus the button to show the preview. Press Escape or blur to dismiss.',
      code: `
        <webmodule-preview placement="top">
          <webmodule-button>Hover or Focus Me</webmodule-button>
          <span slot="title">Preview Title</span>
          <div slot="content">This is the preview content that appears on hover or focus.</div>
        </webmodule-preview>`
    })}
    ${codeExample({
      label: 'Placement',
      description: 'The preview supports twelve placement options, matching tooltip and popup.',
      code: `
        <div class="demo-row">
          <webmodule-preview placement="top">
            <webmodule-button size="small">Top</webmodule-button>
            <span slot="title">Top Placement</span>
            <div slot="content">Preview positioned above the trigger.</div>
          </webmodule-preview>
          <webmodule-preview placement="bottom">
            <webmodule-button size="small">Bottom</webmodule-button>
            <span slot="title">Bottom Placement</span>
            <div slot="content">Preview positioned below the trigger.</div>
          </webmodule-preview>
          <webmodule-preview placement="left">
            <webmodule-button size="small">Left</webmodule-button>
            <span slot="title">Left Placement</span>
            <div slot="content">Preview positioned to the left of the trigger.</div>
          </webmodule-preview>
          <webmodule-preview placement="right">
            <webmodule-button size="small">Right</webmodule-button>
            <span slot="title">Right Placement</span>
            <div slot="content">Preview positioned to the right of the trigger.</div>
          </webmodule-preview>
        </div>`
    })}
    ${codeExample({
      label: 'Scrollable content',
      description: html`The content area scrolls when exceeding <code>--max-height</code> (default: 200px). Content
        preserves line breaks.`,
      code: `
        <webmodule-preview placement="top">
          <webmodule-button>Long Content</webmodule-button>
          <span slot="title">Notes to Client</span>
          <div slot="content">Line 1: First item
Line 2: Second item
Line 3: Third item
Line 4: Fourth item
Line 5: Fifth item
Line 6: Sixth item
Line 7: Seventh item
Line 8: This content will scroll if it exceeds the max height.</div>
        </webmodule-preview>`
    })}
    ${codeExample({
      label: 'CSS custom properties',
      description: html`Tune sizing and timing via <code>--max-width</code>, <code>--max-height</code>,
        <code>--show-delay</code>, and <code>--hide-delay</code>.`,
      code: `
        <div class="demo-row">
          <webmodule-preview style="--max-width: 600px; --max-height: 300px;">
            <webmodule-button>Large Preview</webmodule-button>
            <span slot="title">Custom Size</span>
            <div slot="content">This preview has a custom max-width of 600px and max-height of 300px.</div>
          </webmodule-preview>
          <webmodule-preview style="--show-delay: 500ms; --hide-delay: 200ms;">
            <webmodule-button>Delayed</webmodule-button>
            <span slot="title">Custom Timing</span>
            <div slot="content">This preview has a 500ms show delay and 200ms hide delay.</div>
          </webmodule-preview>
        </div>`
    })}
    ${codeExample({
      label: 'Disabled state',
      description: 'A disabled preview will not show on hover or focus.',
      code: `
        <div class="demo-row">
          <webmodule-preview>
            <webmodule-button>Enabled</webmodule-button>
            <span slot="title">Enabled Preview</span>
            <div slot="content">This preview is enabled and will show.</div>
          </webmodule-preview>
          <webmodule-preview disabled>
            <webmodule-button>Disabled</webmodule-button>
            <span slot="title">Disabled Preview</span>
            <div slot="content">This preview is disabled and will not show.</div>
          </webmodule-preview>
        </div>`
    })}
    ${codeExample({
      label: 'Hoist mode',
      description: html`Use <code>hoist</code> to prevent clipping in scrollable containers via fixed positioning.`,
      code: `
        <webmodule-preview placement="top" hoist>
          <webmodule-button>Hoisted Preview</webmodule-button>
          <span slot="title">Fixed Positioning</span>
          <div slot="content">This preview uses fixed positioning and won't be clipped by overflow containers.</div>
        </webmodule-preview>`
    })}
  `
};
